import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Fish, MapPin, Loader2, Search, Crosshair, ExternalLink, Tent, Home,
  ChevronRight, AlertCircle,
} from "lucide-react";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * /fisheries — Manual "find your local fishery" search.
 *
 * Three origin choices, switchable on the fly:
 *   1. Home / postcode  (UK postcode lookup → postcodes.io)
 *   2. Your current location (Geolocation API)
 *   3. A saved campsite (favourites list, or anything you've viewed)
 *
 * Result list + map of fisheries within a chosen radius, sorted by
 * distance. Each pin's popup has:
 *   - Distance from origin
 *   - "Open in Google Maps" (works on mobile + desktop)
 *   - "Open in Apple Maps" (iOS shortcut)
 *   - "Copy coordinates" for any other navigation app
 *
 * Backed by GET /api/fisheries/search?lat=&lng=&radius_km= (Overpass-live).
 */

const DEFAULT_RADIUS = 25;
const UK_CENTER = [54.0, -2.5];

const pinIcon = L.divIcon({
  className: "cc-fish-pin",
  html: `<div style="background:#16a34a;border:3px solid #fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4)"><span style="color:#fff;font-size:14px;line-height:1">🎣</span></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const originIcon = L.divIcon({
  className: "cc-origin-pin",
  html: `<div style="background:#ff4500;border:3px solid #fff;border-radius:50%;width:24px;height:24px;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

function FitToBounds({ origin, fisheries }) {
  const map = useMap();
  useEffect(() => {
    if (!origin) return;
    const points = [[origin.lat, origin.lng]];
    fisheries.forEach((f) => {
      if (f.lat && f.lng) points.push([f.lat, f.lng]);
    });
    if (points.length === 1) {
      map.setView(points[0], 11);
    } else {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 12 });
    }
  }, [map, origin, fisheries]);
  return null;
}

function gmapsUrl(f) {
  return `https://www.google.com/maps/search/?api=1&query=${f.lat},${f.lng}`;
}
function appleMapsUrl(f) {
  return `https://maps.apple.com/?q=${encodeURIComponent(f.name)}&ll=${f.lat},${f.lng}`;
}

export default function Fisheries() {
  const { user } = useAuth();
  const [origin, setOrigin] = useState(null); // { lat, lng, label }
  const [originMode, setOriginMode] = useState(""); // "postcode" | "geo" | "campsite"
  const [postcode, setPostcode] = useState("");
  const [fisheries, setFisheries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [campsiteOptions, setCampsiteOptions] = useState([]);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [error, setError] = useState("");

  // When user is logged in, fetch their favourites so they can pick one
  // as the origin. The choice is cached for the rest of the session.
  const loadFavorites = React.useCallback(async (aliveRef) => {
    try {
      const r = await api.get("/me/favorites");
      if (!aliveRef.current) return;
      const favs = (r.data?.favorites || r.data || []).slice(0, 30);
      setCampsiteOptions(favs);
    } catch (_e) { /* silent */ }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { if (!user) return undefined; const aliveRef = { current: true }; loadFavorites(aliveRef); return () => { aliveRef.current = false; }; }, [user]);

  const loadFisheries = React.useCallback(async (aliveRef, lat, lng, r) => {
    try {
      const resp = await api.get(`/fisheries/search?lat=${lat}&lng=${lng}&radius_km=${r}`);
      if (!aliveRef.current) return;
      setFisheries(resp.data?.fisheries || []);
    } catch (e) {
      if (!aliveRef.current) return;
      setError("Couldn't fetch fisheries — OpenStreetMap may be busy. Try again in a moment.");
      setFisheries([]);
      console.warn("[fisheries] search failed", e);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { if (!origin) return undefined; const aliveRef = { current: true }; setLoading(true); setError(""); loadFisheries(aliveRef, origin.lat, origin.lng, radius); return () => { aliveRef.current = false; }; }, [origin?.lat, origin?.lng, radius]);

  const lookupPostcode = async (e) => {
    e?.preventDefault?.();
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/fisheries/geocode-postcode?postcode=${encodeURIComponent(pc)}`);
      const d = r.data;
      if (d?.lat && d?.lng) {
        setOrigin({ lat: d.lat, lng: d.lng, label: `${d.postcode}${d.place ? ` · ${d.place}` : ""}` });
        setOriginMode("postcode");
      } else {
        setError("Postcode not found — double-check the spelling?");
      }
    } catch (_e) {
      setError("Postcode not found — double-check the spelling?");
    } finally {
      setLoading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: "Your current location",
        });
        setOriginMode("geo");
        setLoading(false);
      },
      (_e) => {
        toast.error("Couldn't read your location — check browser permissions.");
        setLoading(false);
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const pickCampsite = (site) => {
    if (!site?.lat || !site?.lng) {
      toast.error("That campsite has no coordinates yet.");
      return;
    }
    setOrigin({ lat: site.lat, lng: site.lng, label: site.name });
    setOriginMode("campsite");
    setPickerOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#fffbf2]" data-testid="fisheries-page">
      <Header active="fisheries" />

      <section className="bg-[#0e0e0e] text-white py-12 sm:py-16">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#16a34a] text-white mono uppercase tracking-[0.22em] text-[10px] font-bold mb-4">
            <Fish className="w-3 h-3" /> Camp × Fish
          </div>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight uppercase leading-[0.95] mb-3">
            Find your local <span className="text-[#16a34a]">fishery</span>
          </h1>
          <p className="text-white/85 max-w-xl text-sm sm:text-base">
            Search by postcode, your current location, or any campsite you&apos;ve saved. Drop pins, copy them to Google or Apple Maps, see the distance from your chosen start point.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-5 sm:px-8 py-8">
        {/* Origin chooser */}
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <form onSubmit={lookupPostcode} className="bg-white border-2 border-[#0e0e0e] p-3 flex items-center gap-2" data-testid="origin-postcode">
            <Home className="w-4 h-4 text-[#666] flex-shrink-0" />
            <input
              type="text"
              placeholder="UK postcode e.g. BB3 0RP"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              className="flex-1 min-w-0 outline-none text-sm font-mono uppercase tracking-wider"
            />
            <button type="submit" className="px-3 py-1.5 bg-[#ff4500] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#0e0e0e]">
              Go
            </button>
          </form>

          <button
            type="button"
            onClick={useMyLocation}
            className="bg-white border-2 border-[#0e0e0e] p-3 flex items-center gap-2 hover:bg-[#fff4ee] transition-colors"
            data-testid="origin-geolocation"
          >
            <Crosshair className="w-4 h-4 text-[#666]" />
            <span className="font-display text-sm flex-1 text-left">Use my location</span>
            <ChevronRight className="w-3 h-3 text-[#666]" />
          </button>

          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            disabled={!user}
            className="bg-white border-2 border-[#0e0e0e] p-3 flex items-center gap-2 hover:bg-[#fff4ee] disabled:opacity-50 disabled:cursor-not-allowed transition-colors relative"
            data-testid="origin-campsite"
          >
            <Tent className="w-4 h-4 text-[#666]" />
            <span className="font-display text-sm flex-1 text-left">
              From a saved campsite
            </span>
            <ChevronRight className={`w-3 h-3 text-[#666] transition-transform ${pickerOpen ? "rotate-90" : ""}`} />
            {!user && (
              <span className="absolute -bottom-5 left-0 text-[10px] text-[#666]">
                <Link to="/login" className="underline">Log in</Link> to use saved sites
              </span>
            )}
          </button>
        </div>

        {pickerOpen && (
          <div className="bg-white border-2 border-[#0e0e0e] p-4 mb-4 max-h-[260px] overflow-y-auto">
            <div className="eyebrow mb-2">Your saved campsites</div>
            {campsiteOptions.length === 0 ? (
              <div className="text-sm text-[#666]">
                You haven&apos;t saved any campsites yet. Hit ♥ on any listing to save it as a possible origin.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {campsiteOptions.map((s) => (
                  <button
                    key={s.campsite_id || s.id}
                    type="button"
                    onClick={() => pickCampsite(s)}
                    className="flex items-center justify-between gap-2 px-3 py-2 border hairline hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors text-left"
                    data-testid={`pick-campsite-${s.campsite_id || s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate">{s.name}</div>
                      <div className="text-xs text-[#666] truncate">{s.region || s.country || "UK"}</div>
                    </div>
                    <ChevronRight className="w-3 h-3 text-[#ff4500] flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {origin && (
          <div className="flex flex-wrap items-center gap-3 bg-[#fff8ec] border-2 border-[#fbbf24] p-3 mb-4">
            <MapPin className="w-4 h-4 text-[#ff4500] flex-shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              Searching from <strong>{origin.label}</strong>
            </div>
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
              Radius
              <select
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="bg-white border border-[#0e0e0e] px-2 py-1 text-xs font-mono"
                data-testid="radius-select"
              >
                <option value={10}>10 km</option>
                <option value={25}>25 km</option>
                <option value={50}>50 km</option>
                <option value={80}>80 km</option>
              </select>
            </label>
          </div>
        )}

        {error && (
          <div className="bg-[#fef2f2] border-2 border-[#dc2626] text-[#7f1d1d] p-3 mb-4 flex items-start gap-2 text-sm" data-testid="fish-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {!origin && !loading && (
          <div className="bg-white border-2 border-dashed border-[#0e0e0e]/30 p-10 text-center mb-6">
            <Fish className="w-10 h-10 mx-auto text-[#16a34a] mb-3" />
            <div className="font-display text-xl mb-2">Pick a starting point above</div>
            <p className="text-sm text-[#666] max-w-md mx-auto">
              Postcode → fastest. Location → most accurate. Saved campsite → plan around where you&apos;re staying.
            </p>
          </div>
        )}

        {loading && (
          <div className="text-center py-10 text-[#666]" data-testid="fish-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#16a34a] mb-2" />
            Looking for named fisheries within {radius} km…
          </div>
        )}

        {origin && !loading && (
          <div className="grid lg:grid-cols-[1fr_400px] gap-6">
            {/* Map */}
            <div className="bg-[#e5e7eb] border-2 border-[#0e0e0e] overflow-hidden" style={{ height: 500 }}>
              <MapContainer
                center={[origin.lat, origin.lng]}
                zoom={11}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitToBounds origin={origin} fisheries={fisheries} />
                <Marker position={[origin.lat, origin.lng]} icon={originIcon}>
                  <Popup>
                    <strong>{origin.label}</strong><br />
                    <span style={{ fontSize: 11, color: "#666" }}>Your starting point</span>
                  </Popup>
                </Marker>
                {fisheries.map((f, i) => (
                  <Marker key={i} position={[f.lat, f.lng]} icon={pinIcon}>
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{f.name}</div>
                        {f.description && <div style={{ fontSize: 12, color: "#444", marginBottom: 6 }}>{f.description}</div>}
                        <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                          <strong>{f.distance_km?.toFixed(1)} km</strong> from {origin.label}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <a href={gmapsUrl(f)} target="_blank" rel="noopener noreferrer" style={{ color: "#ff4500", fontSize: 12, fontWeight: 700 }}>
                            Open in Google Maps →
                          </a>
                          <a href={appleMapsUrl(f)} target="_blank" rel="noopener noreferrer" style={{ color: "#0e0e0e", fontSize: 12 }}>
                            Open in Apple Maps →
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(`${f.lat}, ${f.lng}`);
                              toast.success("Coordinates copied");
                            }}
                            style={{ background: "none", border: 0, color: "#666", fontSize: 11, textAlign: "left", padding: 0, marginTop: 2, cursor: "pointer", textDecoration: "underline" }}
                          >
                            Copy coordinates
                          </button>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            {/* List */}
            <div className="bg-white border-2 border-[#0e0e0e] overflow-hidden flex flex-col" style={{ maxHeight: 500 }}>
              <div className="px-4 py-3 border-b-2 border-[#0e0e0e] bg-[#fff8ec]">
                <div className="font-display text-lg">
                  {fisheries.length} {fisheries.length === 1 ? "fishery" : "fisheries"} within {radius} km
                </div>
                <div className="text-xs text-[#666] mt-0.5">Sorted by distance from {origin.label}</div>
              </div>
              <div className="overflow-y-auto flex-1">
                {fisheries.length === 0 && (
                  <div className="p-6 text-sm text-[#666] text-center">
                    Nothing named within {radius} km. Try a bigger radius?
                  </div>
                )}
                {fisheries.map((f, i) => (
                  <div key={i} className="px-4 py-3 border-b hairline last:border-b-0" data-testid={`fish-result-${i}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-bold text-sm flex-1 min-w-0">{f.name}</div>
                      <div className="text-xs mono text-[#16a34a] font-bold flex-shrink-0">
                        {f.distance_km?.toFixed(1)} km
                      </div>
                    </div>
                    {f.description && (
                      <div className="text-xs text-[#666] mb-2 leading-snug">{f.description}</div>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <a
                        href={gmapsUrl(f)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#0e0e0e] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#ff4500] transition-colors"
                        data-testid={`fish-gmaps-${i}`}
                      >
                        <ExternalLink className="w-2.5 h-2.5" /> Google Maps
                      </a>
                      <a
                        href={appleMapsUrl(f)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#0e0e0e] text-[#0e0e0e] text-[10px] font-bold uppercase tracking-wider hover:bg-[#0e0e0e] hover:text-white transition-colors"
                      >
                        <ExternalLink className="w-2.5 h-2.5" /> Apple Maps
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`${f.lat}, ${f.lng}`);
                          toast.success("Coordinates copied");
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-[#666] hover:text-[#0e0e0e] underline"
                      >
                        Copy coords
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 p-5 bg-white border-l-4 border-[#16a34a]">
          <div className="text-xs mono uppercase tracking-wider text-[#16a34a] font-bold mb-2">Where the data comes from</div>
          <p className="text-sm text-[#444] leading-relaxed">
            All fisheries are pulled live from OpenStreetMap — community-tagged angling waters, fishing lakes,
            commercial fisheries, and managed waters. If your local fishery isn&apos;t listed, anyone can add it on
            <a href="https://openstreetmap.org" target="_blank" rel="noopener noreferrer" className="text-[#ff4500] font-bold underline ml-1">openstreetmap.org</a> with the tag
            <code className="mono bg-[#fff8ec] px-1 mx-1">leisure=fishing</code> and it&apos;ll appear here within a day or two.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
